import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth.jsx";

import Landing from "./pages/Landing.jsx";
import Login from "./pages/Login.jsx";
import Register from "./pages/Register.jsx";
import Onboarding from "./pages/Onboarding.jsx";
import HomeDash from "./pages/HomeDash.jsx";
import WalkPage from "./pages/Walk.jsx";
import PassivePage from "./pages/Passive.jsx";
import TrainingPage from "./pages/Training.jsx";
import CheckInPage from "./pages/CheckIn.jsx";
import ProgressPage from "./pages/Progress.jsx";
import CoachPage from "./pages/Coach.jsx";
import ChallengesPage from "./pages/Challenges.jsx";
import CommunityPage from "./pages/Community.jsx";
import RewardsPage from "./pages/Rewards.jsx";
import MarketplacePage from "./pages/Marketplace.jsx";
import ProfilePage from "./pages/Profile.jsx";
import PremiumPage from "./pages/Premium.jsx";
import JourneyPage from "./pages/Journey.jsx";
import ActionCenter from "./pages/ActionCenter.jsx";
import PaymentSuccess from "./pages/PaymentSuccess.jsx";
import CorporatePage from "./pages/Corporate.jsx";
import AdminPage from "./pages/Admin.jsx";
import NotificationsPage from "./pages/Notifications.jsx";
import FriendsPage from "./pages/Friends.jsx";
import MessagesPage from "./pages/Messages.jsx";
import MessageThreadPage from "./pages/MessageThread.jsx";
import SettingsIntegrationsPage from "./pages/SettingsIntegrations.jsx";
import SettingsNotificationsPage from "./pages/SettingsNotifications.jsx";
import CircleDetailPage from "./pages/CircleDetail.jsx";
import LibraryPage from "./pages/Library.jsx";
import LessonPlayerPage from "./pages/LessonPlayer.jsx";
import AnnualGoalPage from "./pages/AnnualGoal.jsx";
import BadgesPage from "./pages/Badges.jsx";
import LeaderboardPage from "./pages/Leaderboard.jsx";
import DuelsPage from "./pages/Duels.jsx";
import DuelDetailPage from "./pages/DuelDetail.jsx";
import WellnessPage from "./pages/Wellness.jsx";
import DevicesPage from "./pages/Devices.jsx";
import CoachCompanion from "./components/CoachCompanion.jsx";

function Protected({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="text-white p-10 text-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/home" replace />;
  if (!user.onboarded && window.location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-center" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
          <Route path="/home" element={<Protected><HomeDash /></Protected>} />
          <Route path="/walk" element={<Protected><WalkPage /></Protected>} />
          <Route path="/passive" element={<Protected><PassivePage /></Protected>} />
          <Route path="/training" element={<Protected><TrainingPage /></Protected>} />
          <Route path="/checkin" element={<Protected><CheckInPage /></Protected>} />
          <Route path="/progress" element={<Protected><ProgressPage /></Protected>} />
          <Route path="/coach" element={<Protected><CoachPage /></Protected>} />
          <Route path="/challenges" element={<Protected><ChallengesPage /></Protected>} />
          <Route path="/community" element={<Protected><CommunityPage /></Protected>} />
          <Route path="/rewards" element={<Protected><RewardsPage /></Protected>} />
          <Route path="/marketplace" element={<Protected><MarketplacePage /></Protected>} />
          <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
          <Route path="/premium" element={<Protected><PremiumPage /></Protected>} />
          <Route path="/journey" element={<Protected><JourneyPage /></Protected>} />
          <Route path="/action" element={<Protected><ActionCenter /></Protected>} />
          <Route path="/payment-success" element={<Protected><PaymentSuccess /></Protected>} />
          <Route path="/corporate" element={<Protected><CorporatePage /></Protected>} />
          <Route path="/admin" element={<Protected adminOnly><AdminPage /></Protected>} />
          <Route path="/notifications" element={<Protected><NotificationsPage /></Protected>} />
          <Route path="/friends" element={<Protected><FriendsPage /></Protected>} />
          <Route path="/messages" element={<Protected><MessagesPage /></Protected>} />
          <Route path="/messages/:friendId" element={<Protected><MessageThreadPage /></Protected>} />
          <Route path="/settings/integrations" element={<Protected><SettingsIntegrationsPage /></Protected>} />
          <Route path="/settings/notifications" element={<Protected><SettingsNotificationsPage /></Protected>} />
          <Route path="/circles/:circleId" element={<Protected><CircleDetailPage /></Protected>} />
          <Route path="/library" element={<Protected><LibraryPage /></Protected>} />
          <Route path="/library/:lessonId" element={<Protected><LessonPlayerPage /></Protected>} />
          <Route path="/annual-goal" element={<Protected><AnnualGoalPage /></Protected>} />
          <Route path="/badges" element={<Protected><BadgesPage /></Protected>} />
          <Route path="/leaderboard" element={<Protected><LeaderboardPage /></Protected>} />
          <Route path="/duels" element={<Protected><DuelsPage /></Protected>} />
          <Route path="/duels/:duelId" element={<Protected><DuelDetailPage /></Protected>} />
          <Route path="/wellness" element={<Protected><WellnessPage /></Protected>} />
          <Route path="/devices" element={<Protected><DevicesPage /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CoachCompanion />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
